<template>
  <Modal
    :value="show"
    max-width="550px"
    :show-close-button="false"
    persistent
    @input="onModalInput"
  >
    <template #header>
      <div class="d-flex flex-column mb-4" style="width: 100%">
        <div class="d-flex justify-space-between align-center">
          <Typography variant="h-5">{{
            isCreateMode ? "New Resource" : "Replace Media"
          }}</Typography>
          <Button icon @click="handleCancel" :disabled="loading">
            <Icon icon="mdi-close" color="mediumGrey" />
          </Button>
        </div>
      </div>
    </template>

    <div v-if="!isCreateMode && content" class="mb-4">
      <Typography variant="subtitle-2" class="text-left">
        Replacing: {{ content.name || content.displayName || "Untitled" }}
      </Typography>
      <Typography variant="caption-2" class="mediumGrey--text text-left">
        Type: {{ content.fileType || "Unknown" }}
        <span v-if="formattedLastUploaded"> &bull; Last Uploaded: {{ formattedLastUploaded }}</span>
      </Typography>
    </div>

    <DragAndDropFileInput
      :file="selectedFile"
      :title="
        isCreateMode ? 'Drag and drop your file here' : 'Drag and drop your replacement file here'
      "
      button-text="Browse Files"
      @file-selected="onFileSelected"
    />

    <v-text-field
      v-if="isCreateMode"
      outlined
      dense
      v-model="name"
      :rules="[(v) => !!v || 'Resource name is required']"
      label="Name*"
      required
      class="mt-4"
    ></v-text-field>

    <div class="d-flex justify-end mt-6 gap-3">
      <Button text @click="handleCancel" :disabled="loading">Cancel</Button>
      <Button @click="handleSubmit" :disabled="!canSubmit" :loading="loading">
        {{ isCreateMode ? "Create Resource" : "Replace" }}
      </Button>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/generic/Modal.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import DragAndDropFileInput from "@/components/DragAndDropFileInput.vue";
import ContentService from "@/services/ContentService";
import moment from "moment";

export default {
  name: "ContentUploadDialog",
  components: {
    Modal,
    Typography,
    Button,
    Icon,
    DragAndDropFileInput,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    content: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: "replace",
      validator: (v) => ["replace", "create"].includes(v),
    },
    clientId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      selectedFile: null,
      name: "",
      loading: false,
    };
  },
  computed: {
    isCreateMode() {
      return this.mode === "create";
    },
    formattedLastUploaded() {
      if (!this.content?.updatedAt) return null;
      return moment(this.content.updatedAt).format("MMM DD, YYYY [at] h:mm A");
    },
    canSubmit() {
      if (this.isCreateMode) return this.selectedFile && this.name?.trim();
      return this.selectedFile;
    },
  },
  watch: {
    show(val) {
      if (!val) {
        this.selectedFile = null;
        this.name = "";
        this.loading = false;
      }
    },
  },
  methods: {
    onModalInput(val) {
      if (!val) this.handleCancel();
    },
    onFileSelected(file) {
      this.selectedFile = file;
      if (this.isCreateMode && !this.name && file?.name) {
        this.name = file.name.replace(/\.[^/.]+$/, "");
      }
    },
    handleCancel() {
      this.selectedFile = null;
      this.name = "";
      this.$emit("update:show", false);
    },
    async handleSubmit() {
      if (this.isCreateMode) {
        await this.handleCreate();
      } else {
        await this.handleReplace();
      }
    },
    async handleCreate() {
      if (!this.selectedFile || !this.name?.trim()) return;
      this.loading = true;
      try {
        const obj = {
          name: this.name.trim(),
          description: "",
          category: "GENERAL_UPLOAD",
          file: this.selectedFile,
          fileName: this.selectedFile.name,
          fileSize: this.selectedFile.size,
          type: "programs",
        };
        if (this.clientId !== null) {
          obj.clientId = this.clientId;
        }
        const response = await ContentService.createContent(obj, 2);
        if (!response || response instanceof Error || response?.isAxiosError) {
          throw response || new Error("Content creation failed");
        }
        this.$emit("created", response);
        this.$emit("update:show", false);
      } catch (error) {
        console.log("Error creating content", error);
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    async handleReplace() {
      if (!this.selectedFile || !this.content) return;
      this.loading = true;
      try {
        const updatedContent = await ContentService.replaceContent(
          this.content.contentId,
          this.selectedFile
        );
        this.$emit("replaced", updatedContent);
        this.$emit("update:show", false);
      } catch (error) {
        console.log("Error replacing content", error);
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.gap-3 {
  gap: 12px;
}
</style>
